import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import { getOidcLogoutUrl } from '@buffer-mono/shared-utils/urls'
import { Button, toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import ConfirmPasswordModal from '../../shared/components/ConfirmPasswordModal'
import ConfirmRemoveAccountModal from '../../Team/components/ConfirmRemoveAccountModal'
import { SettingsRow } from '../../shared/components/SettingsRow'

export const REMOVE_ACCOUNT_MUTATION = graphql(/* GraphQL */ `
  mutation RemoveAccount($input: RemoveAccountInput!) {
    removeAccount(input: $input) {
      ... on RemoveAccountSuccess {
        _empty
      }
      ... on UnexpectedError {
        message
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
    }
  }
`)

export const VERIFY_PASSSWORD_MUTATION = graphql(/* GraphQL */ `
  mutation VerifyPassword($input: VerifyPasswordInput!) {
    verifyPassword(input: $input) {
      ... on VerifyPasswordSuccess {
        _empty
      }
      ... on UnexpectedError {
        message
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
    }
  }
`)

const DeleteAccount = (): JSX.Element => {
  const [
    removeAccount,
    {
      data: removeAccountData,
      error: removeAccountError,
      loading: removeAccountLoading,
    },
  ] = useMutation(REMOVE_ACCOUNT_MUTATION)
  const [
    verifyPassword,
    {
      data: verifyPasswordData,
      error: verifyPasswordError,
      loading: verifyPasswordLoading,
    },
  ] = useMutation(VERIFY_PASSSWORD_MUTATION)

  const loading = removeAccountLoading || verifyPasswordLoading
  const [showModal, setShowModal] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (
      removeAccountData?.removeAccount?.__typename === 'RemoveAccountSuccess'
    ) {
      toast.success('Your account has been deleted. You will be logged out.')
      window.location.assign(getOidcLogoutUrl())
    } else if (removeAccountError || removeAccountData?.removeAccount) {
      toast.error(
        'An error occurred while deleting your account. ' +
          removeAccountError?.message,
      )
    }
  }, [removeAccountData, removeAccountError])

  useEffect(() => {
    if (
      verifyPasswordData?.verifyPassword?.__typename === 'VerifyPasswordSuccess'
    ) {
      setIsPasswordModalOpen(false)
      setShowModal(true)
    } else if (verifyPasswordData?.verifyPassword?.__typename) {
      toast.error('Please ensure you have entered the correct password.')
      setIsPasswordModalOpen(false)
    }
  }, [verifyPasswordData, verifyPasswordError])

  return (
    <>
      <SettingsRow id="delete-account">
        <SettingsRow.Header>
          <SettingsRow.Title>Delete your account</SettingsRow.Title>
          <SettingsRow.Description>
            When you delete your account, you lose access to Buffer account
            services, and we permanently delete your personal data.
          </SettingsRow.Description>
        </SettingsRow.Header>
        <SettingsRow.Control>
          {(ids) => (
            <ConfirmPasswordModal
              open={isPasswordModalOpen}
              onOpenChange={setIsPasswordModalOpen}
              description={
                'To delete your account, please confirm your password.'
              }
              onConfirm={(enteredPassword: string): void => {
                setPassword(enteredPassword)
                void verifyPassword({
                  variables: {
                    input: {
                      password: enteredPassword,
                    },
                  },
                })
              }}
              isLoading={loading}
            >
              <Button
                size="medium"
                variant="critical"
                disabled={loading}
                onClick={() => setIsPasswordModalOpen(true)}
                aria-describedby={ids.descriptionId}
              >
                {loading ? 'Please wait...' : 'Delete Account'}
              </Button>
            </ConfirmPasswordModal>
          )}
        </SettingsRow.Control>
      </SettingsRow>
      <ConfirmRemoveAccountModal
        isOpen={showModal}
        close={(): void => {
          setShowModal(false)
          setPassword('')
        }}
        onConfirm={({ reason, message }): void => {
          setShowModal(false)
          void removeAccount({
            variables: {
              input: {
                reason: {
                  reason,
                  message,
                },
                password,
              },
            },
          })
          setPassword('')
        }}
      />
    </>
  )
}

export default DeleteAccount
